import React from 'react';
import { useSettings } from './context';
import { S } from '../../styles';
import PartsOrderEmailSettings from './PartsOrderEmailSettings';

export default function SettingsWorkflow() {
  const { T, addItemToTemplate, allStatuses, createBlankTemplate, deleteTemplate, editingTemplate, getStatusCategory, importFromItem, isDark, laborItems, loadLaborItems, loadingItems, newItemLabel, newTemplateName, parsePasteText, pasteText, removeItemFromTemplate, saveTemplate, setEditingTemplate, setLaborItems, setNewItemLabel, setNewTemplateName, setPasteText, setSaved, setSettings, setShowNewTemplate, setShowPaste, setStatusCategory, settings, showNewTemplate, showPaste, templates, theme } = useSettings();
  return (
    <>
      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Status Categories</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Assign each Lightspeed status to a category. <strong style={{ color:'#3498db' }}>Risk</strong> statuses appear in the Risk Queue. <strong style={{ color:'#2ecc71' }}>Pickup</strong> statuses appear in Ready for Pickup. <strong style={{ color:'#888' }}>Hidden</strong> statuses are not in use and are excluded everywhere.
        </div>
        {/* Category legend */}
        <div style={{ display:'flex', gap:24, marginBottom:20, padding:'12px 16px', background:T.noteBox, borderRadius:8, border:`1px solid ${T.border}`, alignItems:'center' }}>
          <span style={{ fontSize:11, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.08em', marginRight:4 }}>Key:</span>
          {[
            { key:'active', label:'Risk',   color:'#3498db', desc:'Risk Queue' },
            { key:'pickup', label:'Pickup', color:'#2ecc71', desc:'Ready for Pickup' },
            { key:'review', label:'Review', color:'#9b59b6', desc:'Needs Review' },
            { key:'closed', label:'Hidden', color:'#888',    desc:'Not in use' },
          ].map(cat => (
            <div key={cat.key} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20, background:`${cat.color}22`, color:cat.color, border:`1px solid ${cat.color}` }}>{cat.label}</span>
              <span style={{ fontSize:11, color:T.textSub }}>{cat.desc}</span>
            </div>
          ))}
        </div>
        <div style={S.statusGrid}>
          {allStatuses
            .filter(s => {
              const n = s.name.toLowerCase();
              return n !== 'done & paid' && n !== 'finished'; // Lightspeed system statuses — auto-managed by Spinboard
            })
            .map(s => {
            const category = getStatusCategory(s.name);
            const catConfig = {
              active: { color:'#3498db', bg: theme==='light'?'#eff6ff':'#0d1e2e', border: theme==='light'?'#bfdbfe':'#1a4a6a', label:'RISK' },
              pickup: { color:'#2ecc71', bg: theme==='light'?'#f0fdf4':'#0d2218', border: theme==='light'?'#bbf7d0':'#1a4a30', label:'PICKUP' },
              review: { color:'#9b59b6', bg: theme==='light'?'#faf5ff':'#1a0d2e', border: theme==='light'?'#d8b4fe':'#3a1a5a', label:'REVIEW' },
              closed: { color:'#888',    bg: theme==='light'?'#f5f5f0':'#1e2a40', border: theme==='light'?'#d0d0c8':'#2e3d58', label:'HIDDEN' },
            };
            const cfg = catConfig[category] || catConfig.active;
            return (
              <div key={s.id} style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:10, padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:8 }}>{s.name}</div>
                  <div style={{ display:'flex', gap:6 }}>
                    {[
                      { key:'active', label:'Risk',   color:'#3498db' },
                      { key:'pickup', label:'Pickup', color:'#2ecc71' },
                      { key:'review', label:'Review', color:'#9b59b6' },
                      { key:'closed', label:'Hidden', color:'#888' },
                    ].map(cat => (
                      <button key={cat.key} onClick={() => setStatusCategory(s.name, cat.key)}
                        style={{ fontSize:11, padding:'4px 12px', borderRadius:4,
                          border: category===cat.key ? `1px solid ${cat.color}` : `1px solid ${T.inputBorder}`,
                          cursor:'pointer', fontFamily:"'Inter',sans-serif", fontWeight: category===cat.key ? 700 : 400,
                          background: category===cat.key ? `${cat.color}22` : 'transparent',
                          color: category===cat.key ? cat.color : T.textSub,
                          transition:'all 0.15s',
                        }}>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ fontSize:11, color:cfg.color, fontWeight:700, marginLeft:12, textAlign:'right', minWidth:52 }}>
                  {cfg.label}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize:11, color:T.textSub, marginTop:12, fontStyle:'italic' }}>
          ⓘ "Done & Paid" and "Finished" are Lightspeed system statuses automatically managed by Spinboard and are not configurable.
        </div>
      </div>

      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Workbench — Finalization Statuses</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Select which statuses techs can assign when submitting a completed checklist. Only these statuses will appear in the Workbench checklist submit dropdown.
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
          {allStatuses
            .filter(s => {
              const n = s.name.toLowerCase();
              return n !== 'done & paid' && n !== 'finished' && n !== 'finished/paid' && n !== 'invoiced';
            })
            .map(s => {
              const techFinal = settings.techFinalizationStatuses || [];
              const isSelected = techFinal.includes(s.name.toLowerCase());
              return (
                <label key={s.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderRadius:8, cursor:'pointer',
                  background: isSelected ? '#F4622A22' : T.bg,
                  border:`1px solid ${isSelected ? '#F4622A' : T.border}`, transition:'all 0.15s' }}>
                  <input type="checkbox" checked={isSelected}
                    onChange={() => {
                      const current = settings.techFinalizationStatuses || [];
                      const name = s.name.toLowerCase();
                      const updated = isSelected ? current.filter(n => n !== name) : [...current, name];
                      setSettings({ ...settings, techFinalizationStatuses: updated });
                      setSaved(false);
                    }}
                    style={{ accentColor:'#F4622A', width:14, height:14 }} />
                  <span style={{ fontSize:13, color:T.text }}>{s.name}</span>
                </label>
              );
            })}
        </div>
        {(settings.techFinalizationStatuses || []).length === 0 && (
          <div style={{ fontSize:12, color:'#e67e22' }}>No statuses selected — techs will see all statuses in the checklist dropdown.</div>
        )}
      </div>

      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Quotes</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>How long a new quote or estimate is good for, and where its ticket goes on the customer's answer. Staff can change the date on any quote. An estimate is a ticket sent as written up, with Send estimate on the ticket.</div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Quotes expire after</span>
          <input style={{ ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }} type="number" min="1" max="180"
            value={(settings.quotes && settings.quotes.expiryDays) || 14}
            onChange={e => { setSettings({ ...settings, quotes: { ...(settings.quotes || {}), expiryDays: Math.max(1, Math.min(180, parseInt(e.target.value, 10) || 14)) } }); setSaved(false); }} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>days</span>
        </div>
        {/* Slice 3 (23 Oct): where an approved quote's ticket goes. Open until
            the shop picks; finished, paid and cancelled statuses are never
            offered (server: helpers/quoteApproval.js statusAllowed). */}
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Approved quotes go to</span>
          <select value={(settings.quotes && settings.quotes.approvedStatusId) || ''}
            onChange={e => { setSettings({ ...settings, quotes: { ...(settings.quotes || {}), approvedStatusId: e.target.value || null } }); setSaved(false); }}
            style={{ padding:'6px 8px', borderRadius:6, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text, fontSize:13 }}>
            <option value="">Open (the default)</option>
            {allStatuses
              .filter(st => {
                const n = String(st.name || '').toLowerCase();
                if (n.includes('done') && n.includes('paid')) return false;
                return !['finished', 'invoiced', 'cancelled', 'canceled', 'archived'].some(x => n.includes(x));
              })
              .map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
          </select>
        </div>
        {/* Estimates (26 Oct, Manny: user defined). Send estimate on a ticket
            sends it as written up; these say where the ticket goes on the
            customer's answer. Don't change = leave the status alone. */}
        {[['estimateRequestedStatusId', 'When a tech requests an estimate, move the ticket to'], ['estimateApprovedStatusId', 'Approved estimates go to'], ['estimateDeclinedStatusId', '"Not now" on an estimate goes to']].map(([key, label]) => (
          <div key={key} style={S.thresholdRow}>
            <span style={{ ...S.thresholdLabel, color:T.text }}>{label}</span>
            <select value={(settings.quotes && settings.quotes[key]) || ''}
              onChange={e => { setSettings({ ...settings, quotes: { ...(settings.quotes || {}), [key]: e.target.value || null } }); setSaved(false); }}
              style={{ padding:'6px 8px', borderRadius:6, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text, fontSize:13 }}>
              <option value="">Don't change the status</option>
              {allStatuses
                .filter(st => {
                  const n = String(st.name || '').toLowerCase();
                  if (n.includes('done') && n.includes('paid')) return false;
                  return !['finished', 'invoiced', 'cancelled', 'canceled', 'archived'].some(x => n.includes(x));
                })
                .map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
            </select>
          </div>
        ))}
        <div style={{ fontSize:11, color:T.textSub, marginTop:4 }}>
          When a customer approves, Spinboard writes the ticket in this status. Extra work quoted on an open ticket moves that ticket here too.
        </div>
      </div>

      <PartsOrderEmailSettings T={T} S={S} settings={settings} setSettings={setSettings} setSaved={setSaved} />

      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Risk Thresholds</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>Configure when risk flags are triggered.</div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Flag ticket as stale after</span>
          <input style={{ ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }} type="number" min="1" max="30" value={settings.staleThresholdDays} onChange={e => { setSettings({...settings, staleThresholdDays: parseInt(e.target.value)||4}); setSaved(false); }} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>days open</span>
        </div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Flag waiting ticket after</span>
          <input style={{ ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }} type="number" min="1" max="30" value={settings.waitingThresholdDays} onChange={e => { setSettings({...settings, waitingThresholdDays: parseInt(e.target.value)||3}); setSaved(false); }} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>days waiting</span>
        </div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>Grace period for new tickets</span>
          <input style={{ ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }} type="number" min="0" max="7" value={settings.newTicketGraceDays} onChange={e => { setSettings({...settings, newTicketGraceDays: parseInt(e.target.value)||1}); setSaved(false); }} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>days before flagging "No entries"</span>
        </div>
        <div style={S.thresholdRow}>
          <span style={{ ...S.thresholdLabel, color:T.text }}>High value ticket threshold</span>
          <input style={{ ...S.thresholdInput, width:80, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text }} type="number" min="0" step="50" value={settings.highValueThreshold} onChange={e => { setSettings({...settings, highValueThreshold: parseInt(e.target.value)||400}); setSaved(false); }} />
          <span style={{ ...S.thresholdUnit, color:T.textSub }}>dollars — tickets at or above this are flagged 💰</span>
        </div>
      </div>

      {/* ── Service Templates ── */}
      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Service Templates</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Create checklist templates for the Workbench. Import from your Lightspeed labor items or build from scratch.
        </div>

        {/* Template list */}
        {templates.length > 0 && !editingTemplate && (
          <div style={{ display:'grid', gap:8, marginBottom:16 }}>
            {templates.map(tpl => (
              <div key={tpl.id} style={{ background: isDark ? '#1e2a40' : '#f4f4ef', borderRadius:8, padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div>
                  <div style={{ fontSize:14, fontWeight:600, color:T.text }}>{tpl.name}</div>
                  <div style={{ fontSize:12, color:T.textSub, marginTop:2 }}>{tpl.items.length} items</div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => setEditingTemplate({ ...tpl })} style={{ ...S.btn(false), fontSize:12, padding:'5px 12px', color:T.textSub, borderColor:T.border }}>Edit</button>
                  <button onClick={() => deleteTemplate(tpl.id)} style={{ ...S.btn(false), fontSize:12, padding:'5px 12px', color:'#e74c3c', borderColor:'#e74c3c33' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit template */}
        {editingTemplate && (
          <div style={{ background: isDark ? '#1e2a40' : '#f4f4ef', borderRadius:10, padding:'16px', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <input
                value={editingTemplate.name}
                onChange={e => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'6px 10px', color:T.text, fontSize:14, fontWeight:600, fontFamily:'Arial, sans-serif', outline:'none', flex:1, marginRight:12 }}
              />
            </div>
            <div style={{ display:'grid', gap:6, marginBottom:12 }}>
              {editingTemplate.items.map((item, i) => (
                <div key={item.id} style={{ display:'flex', alignItems:'center', gap:8, background:T.card, borderRadius:6, padding:'8px 12px' }}>
                  <span style={{ fontSize:11, color:T.textMuted, minWidth:20 }}>{i+1}</span>
                  <input
                    value={item.label}
                    onChange={e => setEditingTemplate({ ...editingTemplate, items: editingTemplate.items.map(it => it.id === item.id ? { ...it, label: e.target.value } : it) })}
                    style={{ flex:1, background:'transparent', border:'none', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none' }}
                  />
                  {item.custom && <span style={{ fontSize:10, color:T.textMuted }}>custom</span>}
                  <button onClick={() => removeItemFromTemplate(editingTemplate, item.id)} style={{ background:'transparent', border:'none', color:'#e74c3c', cursor:'pointer', fontSize:14 }}>×</button>
                </div>
              ))}
            </div>
            {/* Paste & parse */}
            {showPaste ? (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:11, color:T.textSub, marginBottom:6 }}>
                  Paste your service description below — each line becomes a checklist item
                </div>
                <textarea
                  autoFocus
                  value={pasteText}
                  onChange={e => setPasteText(e.target.value)}
                  placeholder="Paste description text here…"
                  rows={6}
                  style={{ width:'100%', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'8px 10px', color:T.text, fontSize:12, fontFamily:'Arial, sans-serif', resize:'vertical', outline:'none', boxSizing:'border-box', marginBottom:8 }}
                />
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={parsePasteText} style={{ ...S.btn(true), background:'#F4622A', color:'#fff', fontSize:12, padding:'6px 14px' }}>Parse into items</button>
                  <button onClick={() => { setShowPaste(false); setPasteText(''); }} style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, fontSize:12 }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowPaste(true)} style={{ ...S.btn(false), marginBottom:10, color:'#3498db', borderColor:'#3498db33', fontSize:12 }}>
                📋 Paste & parse description
              </button>
            )}

            {/* Add item manually */}
            <div style={{ display:'flex', gap:8, marginBottom:14 }}>
              <input
                value={newItemLabel}
                onChange={e => setNewItemLabel(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addItemToTemplate(editingTemplate); }}
                placeholder="Add checklist item…"
                style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'6px 10px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none' }}
              />
              <button onClick={() => addItemToTemplate(editingTemplate)} style={{ ...S.btn(true), background:'#F4622A', color:'#fff', padding:'6px 14px', fontSize:12 }}>Add</button>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => saveTemplate(editingTemplate)} style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText, fontSize:12 }}>Save Template</button>
              <button onClick={() => setEditingTemplate(null)} style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, fontSize:12 }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Actions */}
        {!editingTemplate && (
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {showNewTemplate ? (
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input
                  autoFocus
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') createBlankTemplate(); if (e.key === 'Escape') setShowNewTemplate(false); }}
                  placeholder="Template name…"
                  style={{ background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'6px 10px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none', width:200 }}
                />
                <button onClick={createBlankTemplate} style={{ ...S.btn(true), background:'#F4622A', color:'#fff', fontSize:12, padding:'6px 14px' }}>Create</button>
                <button onClick={() => setShowNewTemplate(false)} style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, fontSize:12 }}>Cancel</button>
              </div>
            ) : (
              <button onClick={() => setShowNewTemplate(true)} style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, fontSize:12 }}>+ New blank template</button>
            )}
            <button
              onClick={() => laborItems.length ? setLaborItems([]) : loadLaborItems()}
              style={{ ...S.btn(false), color:'#3498db', borderColor:'#3498db33', fontSize:12 }}
            >
              {loadingItems ? 'Loading…' : laborItems.length ? 'Hide Lightspeed items' : '↓ Import from Lightspeed'}
            </button>
          </div>
        )}

        {/* Labor items list for import */}
        {laborItems.length > 0 && (
          <div style={{ marginTop:14, borderTop:`1px solid ${T.border}`, paddingTop:14 }}>
            <div style={{ fontSize:11, color:T.textSub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Select a labor item to import as template</div>
            <div style={{ display:'grid', gap:6, maxHeight:240, overflowY:'auto' }}>
              {laborItems.map(item => (
                <div key={item.id} onClick={() => importFromItem(item)}
                  style={{ background:T.card, border:`1px solid ${T.cardBorder}`, borderRadius:8, padding:'10px 14px', cursor:'pointer', transition:'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#F4622A'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = T.cardBorder}
                >
                  <div style={{ fontSize:13, fontWeight:500, color:T.text }}>{item.name}</div>
                  {item.category && <div style={{ fontSize:11, color:T.textSub, marginTop:2 }}>{item.category}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Manager Review Send-Back Reasons ── */}
      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Manager Review — Send-Back Reasons</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          These reasons appear in the Manager Review Wizard when sending a ticket back to the tech. Add, edit, or remove reasons to match your shop's workflow.
        </div>
        <div style={{ display:'grid', gap:8, marginBottom:12 }}>
          {(settings.sendbackReasons || ['More information needed','Missing parts or labor entries','Customer not yet contacted','Incorrect diagnosis — please re-evaluate','Waiting on parts — update status','Estimate not approved yet']).map((reason, i) => (
            <div key={i} style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input
                value={reason}
                onChange={e => {
                  const updated = [...(settings.sendbackReasons || ['More information needed','Missing parts or labor entries','Customer not yet contacted','Incorrect diagnosis — please re-evaluate','Waiting on parts — update status','Estimate not approved yet'])];
                  updated[i] = e.target.value;
                  setSettings({ ...settings, sendbackReasons: updated });
                }}
                style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6,
                  padding:'7px 12px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none' }}
              />
              <button onClick={() => {
                  const updated = (settings.sendbackReasons || []).filter((_, j) => j !== i);
                  setSettings({ ...settings, sendbackReasons: updated });
                }}
                style={{ ...S.btn(false), padding:'6px 10px', color:'#e74c3c', borderColor:'#e74c3c33', fontSize:13, flexShrink:0 }}>
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => {
            const current = settings.sendbackReasons || ['More information needed','Missing parts or labor entries','Customer not yet contacted','Incorrect diagnosis — please re-evaluate','Waiting on parts — update status','Estimate not approved yet'];
            setSettings({ ...settings, sendbackReasons: [...current, ''] });
          }}
          style={{ ...S.btn(false), fontSize:12, color:T.textSub, borderColor:T.border }}>
          + Add reason
        </button>
      </div>

      {/* Tech timer (2 Oct). When a timer left running is stopped for the tech,
          and what the invoice line says when the time is hidden. The server
          side is helpers/techTimer.js autoStopPlan and jobs/timerAutoStop.js. */}
      {(() => {
        const tt = { autoStop: 'nightly', stopAt: '22:00', graceMinutes: 30, neutralLabel: 'Shop labor, included', ...(settings.techTime || {}) };
        const setTT = (patch) => { setSettings({ ...settings, techTime: { ...tt, ...patch } }); setSaved(false); };
        const radio = (value, label, detail) => (
          <label key={value} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 12px', borderRadius:8, cursor:'pointer',
            border:`1px solid ${tt.autoStop === value ? '#3498db' : T.inputBorder}`, background: tt.autoStop === value ? '#3498db14' : 'transparent' }}>
            <input type="radio" name="techTimeAutoStop" checked={tt.autoStop === value} onChange={() => setTT({ autoStop: value })}
              style={{ marginTop:3, accentColor:'#3498db' }} />
            <span style={{ flex:1 }}>
              <span style={{ fontSize:13, fontWeight:600, color:T.text }}>{label}</span>
              <span style={{ display:'block', fontSize:12, color:T.textSub, marginTop:4 }}>{detail}</span>
            </span>
          </label>
        );
        const inputStyle = { ...S.thresholdInput, background:T.input, border:`1px solid ${T.inputBorder}`, color:T.text };
        return (
          <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
            <div style={{ ...S.settingsCardTitle, color:T.text }}>Tech timer</div>
            <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
              When a tech forgets to press Stop, Spinboard stops the timer for them, writes the time to Lightspeed and flags it on My Workbench for a manager to confirm or correct.
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:18 }}>
              {radio('nightly', 'Every night at a set time', (
                <span style={{ display:'inline-flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  Stop any timer still running at
                  <input type="time" value={tt.stopAt} disabled={tt.autoStop !== 'nightly'}
                    onChange={e => setTT({ stopAt: e.target.value || '22:00' })}
                    style={{ ...inputStyle, width:120 }} />
                  every day, open or closed. Best for shops that stay late or work on days off.
                </span>
              ))}
              {radio('close', 'At closing time', (
                <span style={{ display:'inline-flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                  Stop timers
                  <input type="number" min="0" max="240" value={tt.graceMinutes} disabled={tt.autoStop !== 'close'}
                    onChange={e => setTT({ graceMinutes: Math.min(240, Math.max(0, parseInt(e.target.value, 10) || 0)) })}
                    style={{ ...inputStyle, width:70 }} />
                  minutes after the closing time in Shop hours. A timer started after closing runs until the next closing.
                </span>
              ))}
              {radio('off', 'Never', 'Timers run until someone presses Stop.')}
            </div>
            <div style={S.thresholdRow}>
              <span style={{ ...S.thresholdLabel, color:T.text }}>Invoice line when time is hidden</span>
              <input type="text" maxLength={60} value={tt.neutralLabel}
                onChange={e => setTT({ neutralLabel: e.target.value })}
                onBlur={e => { if (!e.target.value.trim()) setTT({ neutralLabel: 'Shop labor, included' }); }}
                style={{ ...inputStyle, width:260, textAlign:'left', fontFamily:'inherit' }} />
            </div>
            <div style={{ fontSize:11, color:T.textSub, marginTop:4, fontStyle:'italic' }}>
              Prints on a $0 labor line when "Show time on invoice" is off for a ticket. Changes apply the next time a ticket's time is written.
            </div>
            <div style={{ ...S.thresholdRow, marginTop:14 }}>
              <span style={{ ...S.thresholdLabel, color:T.text }}>Target labor rate</span>
              <input type="number" min="0" step="1" placeholder="Off" value={tt.targetRate ?? ''}
                onChange={e => setTT({ targetRate: e.target.value === '' ? null : Math.max(0, parseFloat(e.target.value) || 0) })}
                style={{ ...inputStyle, width:90 }} />
              <span style={{ ...S.thresholdUnit, color:T.textSub }}>$ per hour worked. Managers see a ticket flagged when its labor billed works out below this. Leave empty for no flag.</span>
            </div>
          </div>
        );
      })()}
    </>
  );
}
